/**
 * Retrieves data from localStorage or sessionStorage.
 *
 * @param {string} key - The key of the data to retrieve.
 * @param {*} [defaultValue=null] - The default value to return if the key does not exist.
 * @param {string} [type='local'] - The type of storage to use ('local' for localStorage, 'session' for sessionStorage).
 * @returns {*} The parsed data from storage, or the default value if the key does not exist or an error occurs.
 */
export const getStorage = (key, defaultValue = null, type = 'local') => {
  try {
    const cachedData = type === 'session' ? sessionStorage.getItem(key) : localStorage.getItem(key)
    if (cachedData) {
      return JSON.parse(cachedData)
    }
  } catch (error) {
    console.error(error)
  }
  return defaultValue
}

/**
 * Stores a value in either localStorage or sessionStorage.
 *
 * @param {string} key - The key under which the value will be stored.
 * @param {*} [value=null] - The value to be stored. If not provided, the function will return early.
 * @param {string} [type='local'] - The type of storage to use ('local' for localStorage, 'session' for sessionStorage).
 * @throws Will log an error to the console if the storage operation fails.
 */
export const setStorage = (key, value = null, type = 'local') => {
  if (value === undefined || value === null) return
  try {
    const serializedValue = JSON.stringify(value)
    const lastValue = getStorage(key, null, type)
    if (JSON.stringify(lastValue) === serializedValue) return
    if (type === 'local') {
      localStorage.setItem(key, serializedValue)
    } else if (type === 'session') {
      sessionStorage.setItem(key, serializedValue)
    }
  } catch (error) {
    console.error(error)
  }
}
